import { useState, useCallback, useEffect } from 'react';
import { userDashboard } from '../Service/UserService';
import { UserResponse } from '../Model/User';
import { SiteService } from '../Service/SiteService';
import { Site } from '../Model/Site';
import { AwsService } from '../Service/AwsService';

// Types

interface SiteMetrics {
  timestamp: string;
  bandwidth: number;
  responseTime: number;
  requests: number;
}

interface AwsState {
  isConnecting: boolean;
  connectionError: string | null;
  isVerifying: boolean;
  verificationError: string | null;
}

interface SiteState {
  sites: Site[];
  selectedSite: Site | null;
  metrics: SiteMetrics[];
  isLoading: boolean;
  error: string | null;
}

interface SiteViewModelState extends SiteState {
  userData?: UserResponse;
  showAWSModal: boolean;
  aws: AwsState;
}

export interface SiteViewModel {
  // Existing properties
  sites: Site[];
  userData?: UserResponse;
  dashboardData: () => Promise<UserResponse | null>;
  showAWSModal: boolean;
  setShowAWSModal: (show: boolean) => void;
  selectedSite: Site | null;
  metrics: SiteMetrics[];
  isLoading: boolean;
  error: string | null;
  
  // AWS related properties
  isAwsConnecting: boolean;
  awsConnectionError: string | null;
  isAwsVerifying: boolean;
  awsVerificationError: string | null;
  
  // Methods
  fetchSites: () => Promise<void>;
  fetchSiteById: (id: string) => Promise<void>;
  updateSiteStatus: (id: string, action: 'start' | 'stop' | 'restart') => Promise<void>;
  clearError: () => void;
  
  // AWS methods
  createAwsClient: (role_arn: string, region: string) => Promise<{ success: boolean; message: string }>;
  verifyAwsConnection: (role_arn: string) => Promise<{ success: boolean; message: string }>;
  clearAwsErrors: () => void;
}

// Mock API functions (replace with actual API calls)
const mockSiteApi = {
  fetchSites: async (): Promise<Site[]> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return [
    
      // Add more mock sites as needed
    ];
  },

  fetchSiteById: async (id: string): Promise<Site | null> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return null;
  },

  fetchSiteMetrics: async (id: string): Promise<SiteMetrics[]> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const metrics: SiteMetrics[] = [];
    const now = new Date();
    
    // Generate 24 hours of mock metrics
    for (let i = 0; i < 24; i++) {
      metrics.push({
        timestamp: new Date(now.getTime() - (i * 3600000)).toISOString(),
        bandwidth: Math.random() * 5 + 1,
        responseTime: Math.random() * 200 + 100,
        requests: Math.floor(Math.random() * 1000)
      });
    }
    
    return metrics.reverse();
  },

  updateSiteStatus: async (id: string, action: 'start' | 'stop' | 'restart'): Promise<Site | null> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return null;
  }
};

export const useSiteViewModel = (): SiteViewModel => {
  const [siteState, setSiteState] = useState<SiteViewModelState>({
    sites: [],
    selectedSite: null,
    metrics: [],
    isLoading: false,
    error: null,
    userData: undefined,
    showAWSModal: false,
    aws: {
      isConnecting: false,
      connectionError: null,
      isVerifying: false,
      verificationError: null
    }
  });

  
  const setShowAWSModal = useCallback((show: boolean) => {
    setSiteState(prev => ({ ...prev, showAWSModal: show }));
  }, []);

  

  const dashboardData = useCallback(async () => {
    try {
      const res = await userDashboard();
      if (res.status === 200) {
        setSiteState(prev => ({ ...prev, userData: res.data }));
        
        let connectedClouds;
        if (Array.isArray(res.data.connected_clouds)) {
            connectedClouds = res.data.connected_clouds; 
        } else {
            try {
                const jsonString = (res.data.connected_clouds || '[]').replace(/'/g, '"');
                connectedClouds = JSON.parse(jsonString);
            } catch (e) {
                console.error('Error parsing connected_clouds:', e, 'Raw data:', res.data.connected_clouds);
                connectedClouds = [];
            }
        }
        
        // Check if AWS is not in the connected clouds
        if (!connectedClouds.includes('aws')) {
          setShowAWSModal(true);
        }
        
        return res.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return null;
    }
  }, [setShowAWSModal]);
  
   // AWS-related methods
   const handleAwsClient = useCallback(async (role_arn: string, region: string) => {
    setSiteState(prev => ({
      ...prev,
      aws: { ...prev.aws, isConnecting: true, connectionError: null }
    }));

    try {
      const response = await AwsService.createCredentials({role_arn, region});
      
      if (response.success) {
        return { success: true, message: 'AWS client setup successful' };
      } else {
        throw new Error('Failed to create AWS client');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create AWS client';
      setSiteState(prev => ({
        ...prev,
        aws: { ...prev.aws, connectionError: errorMessage }
      }));
      return { success: false, message: errorMessage };
    } finally {
      setSiteState(prev => ({
        ...prev,
        aws: { ...prev.aws, isConnecting: false }
      }));
    }
  }, []);


  const handleAwsVerification = useCallback(async (role_arn: string) => {
    setSiteState(prev => ({
      ...prev,
      aws: { ...prev.aws, isVerifying: true, verificationError: null }
    }));

    try {
      const response = await AwsService.verifyConnection(role_arn);
      
      if (response.success) {
        // Update connected clouds in user data
        
        setSiteState(prev => {
          if (!prev.userData) return prev;
        
          // Convert connected_clouds to string since UserResponse expects it as a string
          const currentClouds = Array.isArray(prev.userData.connected_clouds)
            ? prev.userData.connected_clouds
            : JSON.parse(prev.userData.connected_clouds || '[]');
          
          if (!currentClouds.includes('aws')) {
            const updatedClouds = [...currentClouds, 'aws'];
            return {
              ...prev,
              userData: {
                ...prev.userData,
                // Convert back to string before saving to match UserResponse type
                connected_clouds: JSON.stringify(updatedClouds)
              }
            };
          }
          return prev;
        });
        return { success: true, message: 'AWS connection verified successfully' };
      
      } else {
        throw new Error(response.message || 'Verification failed');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify AWS connection';
      setSiteState(prev => ({
        ...prev,
        aws: { ...prev.aws, verificationError: errorMessage }
      }));
      return { success: false, message: errorMessage };
    } finally {
      setSiteState(prev => ({
        ...prev,
        aws: { ...prev.aws, isVerifying: false }
      }));
    }
  }, []);

  const clearAwsErrors = useCallback(() => {
    setSiteState(prev => ({
      ...prev,
      aws: { ...prev.aws, connectionError: null, verificationError: null }
    }));
  }, []);

  

  // Fetch all sites
  const fetchSites = useCallback(async () => {
    try {
      setSiteState(prev => ({ ...prev, isLoading: true, error: null }));
    
      const sites = await SiteService.getAllSites();
      setSiteState(prev => ({
        ...prev,
        sites,
        isLoading: false
      }));
    } catch (error) {
      setSiteState(prev => ({
        ...prev,
        error: 'Failed to fetch sites',
        isLoading: false
      }));
    }
  }, []);

  // Fetch single site
  const fetchSiteById = useCallback(async (id: string) => {
    try {
      setSiteState(prev => ({ ...prev, isLoading: true, error: null }));
      const site = await mockSiteApi.fetchSiteById(id);
      const metrics = await mockSiteApi.fetchSiteMetrics(id);
      setSiteState(prev => ({
        ...prev,
        selectedSite: site,
        metrics,
        isLoading: false
      }));
    } catch (error) {
      setSiteState(prev => ({
        ...prev,
        error: 'Failed to fetch site details',
        isLoading: false
      }));
    }
  }, []);

  // Site actions (start/stop/restart)
  const updateSiteStatus = useCallback(async (
    id: string,
    action: 'start' | 'stop' | 'restart'
  ) => {
    try {
      setSiteState(prev => ({ ...prev, isLoading: true, error: null }));
      const updatedSite = await mockSiteApi.updateSiteStatus(id, action);
      
      // setSiteState(prev => ({
      //   ...prev,
      //   sites: prev.sites.map(site => 
      //     site.id === id ? updatedSite : site
      //   ),
      //   selectedSite: prev.selectedSite?.id === id ? updatedSite : prev.selectedSite,
      //   isLoading: false
      // }));
    } catch (error) {
      setSiteState(prev => ({
        ...prev,
        error: `Failed to ${action} site`,
        isLoading: false
      }));
    }
  }, []);

  // Start polling metrics when a site is selected
  useEffect(() => {
    if (!siteState.selectedSite) return;

    const pollInterval = setInterval(async () => {
      try {
        const newMetrics = await mockSiteApi.fetchSiteMetrics(siteState.selectedSite!!.id);
        setSiteState(prev => ({
          ...prev,
          metrics: newMetrics
        }));
      } catch (error) {
        console.error('Failed to update metrics:', error);
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [siteState.selectedSite]);

  // Clear error
  const clearError = useCallback(() => {
    setSiteState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // Existing Site properties
    sites: siteState.sites,
    userData: siteState.userData,
    dashboardData,
    showAWSModal: siteState.showAWSModal,
    setShowAWSModal,
    selectedSite: siteState.selectedSite,
    metrics: siteState.metrics,
    isLoading: siteState.isLoading,
    error: siteState.error,
  
    // AWS related properties
    isAwsConnecting: siteState.aws.isConnecting,
    awsConnectionError: siteState.aws.connectionError,
    isAwsVerifying: siteState.aws.isVerifying,
    awsVerificationError: siteState.aws.verificationError,
    
    // Site related methods
    fetchSites,
    fetchSiteById,
    updateSiteStatus,
    clearError,
  
    // AWS related methods
    createAwsClient: handleAwsClient,
    verifyAwsConnection: handleAwsVerification,
    clearAwsErrors
  };
};